import React, { useEffect, useMemo, useState } from "react";
import { fetchFundamentalTrends } from "../utils/fundamentalTrends";

function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function fmtScore(v) {
  return isFiniteNumber(v) ? v.toFixed(1) : "-";
}

function fmtPct(v) {
  return isFiniteNumber(v) ? `${(v * 100).toFixed(1)}%` : "-";
}

function fmtMargin(v) {
  return isFiniteNumber(v) ? `${(v * 100).toFixed(1)}%` : "-";
}

function fmtLargeNumber(v) {
  if (!isFiniteNumber(v)) return "-";

  const abs = Math.abs(v);

  if (abs >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(2)}K`;

  return v.toFixed(0);
}

function compareValues(a, b, direction = "desc") {
  const dir = direction === "asc" ? 1 : -1;

  const aNum = typeof a === "number" && Number.isFinite(a);
  const bNum = typeof b === "number" && Number.isFinite(b);

  if (aNum && bNum) {
    return a < b ? -1 * dir : a > b ? 1 * dir : 0;
  }

  if (aNum && !bNum) return -1;
  if (!aNum && bNum) return 1;

  const aStr = String(a ?? "").toUpperCase();
  const bStr = String(b ?? "").toUpperCase();

  if (aStr < bStr) return -1 * dir;
  if (aStr > bStr) return 1 * dir;
  return 0;
}

export default function FundamentalScores() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("tamScore");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const res = await fetchFundamentalTrends();
        if (!cancelled) {
          setData(res);
          setError(null);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(err.message || "Failed to load fundamental trends");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const stocks = data?.stocks || {};
    return Object.entries(stocks).map(([ticker, item]) => ({
      ticker,
      tamScore: item?.tamScore ?? null,
      moatScore: item?.moatScore ?? null,
      operatingMarginTrendScore: item?.operatingMarginTrendScore ?? null,
      grossMarginTrendScore: item?.grossMarginTrendScore ?? null,
      moatSource: item?.moatSource ?? null,
      error: item?.error ?? null,

      latestDate: item?.revenue?.latestDate ?? null,
      latestPeriod: item?.revenue?.latestPeriod ?? null,
      latestYear: item?.revenue?.latestYear ?? null,

      R0: item?.revenue?.R0 ?? null,
      R1: item?.revenue?.R1 ?? null,
      R2: item?.revenue?.R2 ?? null,
      R3: item?.revenue?.R3 ?? null,

      O0: item?.operatingMargin?.O0 ?? null,
      O1: item?.operatingMargin?.O1 ?? null,
      O2: item?.operatingMargin?.O2 ?? null,
      O3: item?.operatingMargin?.O3 ?? null,

      G0: item?.grossMargin?.G0 ?? null,
      G1: item?.grossMargin?.G1 ?? null,
      G2: item?.grossMargin?.G2 ?? null,
      G3: item?.grossMargin?.G3 ?? null,

      r01: item?.revenue?.r01 ?? null,
      r12: item?.revenue?.r12 ?? null,
      r03: item?.revenue?.r03 ?? null,

      o01: item?.operatingMargin?.o01 ?? null,
      o12: item?.operatingMargin?.o12 ?? null,
      o03: item?.operatingMargin?.o03 ?? null,

      g01: item?.grossMargin?.g01 ?? null,
      g12: item?.grossMargin?.g12 ?? null,
      g03: item?.grossMargin?.g03 ?? null,
    }));
  }, [data]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (!q) return rows;
    return rows.filter((row) => {
      return (
        row.ticker.includes(q) ||
        String(row.moatSource || "").toUpperCase().includes(q) ||
        String(row.latestPeriod || "").toUpperCase().includes(q)
      );
    });
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareValues(a?.[sortKey], b?.[sortKey], sortDirection));
    return arr;
  }, [filteredRows, sortKey, sortDirection]);

  function handleSort(nextKey) {
    if (sortKey === nextKey) {
      setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }
    setSortKey(nextKey);
    setSortDirection("desc");
  }

  function sortIndicator(key) {
    if (sortKey !== key) return "";
    return sortDirection === "desc" ? " ▼" : " ▲";
  }

  return (
    <div style={{ padding: "16px" }}>
      <div
        style={{
          display: "flex",
          gap: "12px",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "16px",
        }}
      >
        <h2 style={{ margin: 0 }}>Fundamental Scores</h2>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ticker..."
          style={{
            padding: "8px 10px",
            minWidth: "220px",
            borderRadius: "8px",
            border: "1px solid #ccc",
          }}
        />

        <div style={{ fontSize: "14px", opacity: 0.8 }}>
          {data?.as_of ? `As of ${data.as_of}` : ""}
        </div>
      </div>

      {loading && <div>Loading fundamental scores...</div>}

      {error && (
        <div style={{ color: "crimson", marginBottom: "12px" }}>
          Failed to load data: {error}
        </div>
      )}

      {!loading && !error && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "2200px",
            }}
          >
            <thead>
              <tr>
                <th style={thStyle} onClick={() => handleSort("ticker")}>
                  Ticker{sortIndicator("ticker")}
                </th>
                <th style={thStyle} onClick={() => handleSort("tamScore")}>
                  TAM{sortIndicator("tamScore")}
                </th>
                <th style={thStyle} onClick={() => handleSort("moatScore")}>
                  MOAT{sortIndicator("moatScore")}
                </th>
                <th style={thStyle} onClick={() => handleSort("operatingMarginTrendScore")}>
                  Op Trend{sortIndicator("operatingMarginTrendScore")}
                </th>
                <th style={thStyle} onClick={() => handleSort("grossMarginTrendScore")}>
                  Gross Trend{sortIndicator("grossMarginTrendScore")}
                </th>
                <th style={thStyle} onClick={() => handleSort("moatSource")}>
                  MOAT Source{sortIndicator("moatSource")}
                </th>

                <th style={thStyle} onClick={() => handleSort("R0")}>
                  R0{sortIndicator("R0")}
                </th>
                <th style={thStyle} onClick={() => handleSort("R1")}>
                  R1{sortIndicator("R1")}
                </th>
                <th style={thStyle} onClick={() => handleSort("R2")}>
                  R2{sortIndicator("R2")}
                </th>
                <th style={thStyle} onClick={() => handleSort("R3")}>
                  R3{sortIndicator("R3")}
                </th>

                <th style={thStyle} onClick={() => handleSort("O0")}>
                  O0{sortIndicator("O0")}
                </th>
                <th style={thStyle} onClick={() => handleSort("O1")}>
                  O1{sortIndicator("O1")}
                </th>
                <th style={thStyle} onClick={() => handleSort("O2")}>
                  O2{sortIndicator("O2")}
                </th>
                <th style={thStyle} onClick={() => handleSort("O3")}>
                  O3{sortIndicator("O3")}
                </th>

                <th style={thStyle} onClick={() => handleSort("G0")}>
                  G0{sortIndicator("G0")}
                </th>
                <th style={thStyle} onClick={() => handleSort("G1")}>
                  G1{sortIndicator("G1")}
                </th>
                <th style={thStyle} onClick={() => handleSort("G2")}>
                  G2{sortIndicator("G2")}
                </th>
                <th style={thStyle} onClick={() => handleSort("G3")}>
                  G3{sortIndicator("G3")}
                </th>

                <th style={thStyle} onClick={() => handleSort("r01")}>
                  Rev Q/Q{sortIndicator("r01")}
                </th>
                <th style={thStyle} onClick={() => handleSort("r12")}>
                  Rev Prev Q/Q{sortIndicator("r12")}
                </th>
                <th style={thStyle} onClick={() => handleSort("r03")}>
                  Rev Y/Y{sortIndicator("r03")}
                </th>

                <th style={thStyle} onClick={() => handleSort("o01")}>
                  Op Δ Q/Q{sortIndicator("o01")}
                </th>
                <th style={thStyle} onClick={() => handleSort("o12")}>
                  Op Δ Prev{sortIndicator("o12")}
                </th>
                <th style={thStyle} onClick={() => handleSort("o03")}>
                  Op Δ Y/Y{sortIndicator("o03")}
                </th>

                <th style={thStyle} onClick={() => handleSort("g01")}>
                  Gross Δ Q/Q{sortIndicator("g01")}
                </th>
                <th style={thStyle} onClick={() => handleSort("g12")}>
                  Gross Δ Prev{sortIndicator("g12")}
                </th>
                <th style={thStyle} onClick={() => handleSort("g03")}>
                  Gross Δ Y/Y{sortIndicator("g03")}
                </th>

                <th style={thStyle} onClick={() => handleSort("latestDate")}>
                  Latest Quarter{sortIndicator("latestDate")}
                </th>
                <th style={thStyle}>Error</th>
              </tr>
            </thead>

            <tbody>
              {sortedRows.map((row) => (
                <tr key={row.ticker}>
                  <td style={tdTickerStyle}>{row.ticker}</td>
                  <td style={tdStyle}>{fmtScore(row.tamScore)}</td>
                  <td style={tdStyle}>{fmtScore(row.moatScore)}</td>
                  <td style={tdStyle}>{fmtScore(row.operatingMarginTrendScore)}</td>
                  <td style={tdStyle}>{fmtScore(row.grossMarginTrendScore)}</td>
                  <td style={tdStyle}>
                    {row.moatSource === "grossMargin"
                      ? "Gross Margin"
                      : row.moatSource === "operatingMargin"
                      ? "Operating Margin"
                      : "-"}
                  </td>

                  <td style={tdStyle}>{fmtLargeNumber(row.R0)}</td>
                  <td style={tdStyle}>{fmtLargeNumber(row.R1)}</td>
                  <td style={tdStyle}>{fmtLargeNumber(row.R2)}</td>
                  <td style={tdStyle}>{fmtLargeNumber(row.R3)}</td>

                  <td style={tdStyle}>{fmtMargin(row.O0)}</td>
                  <td style={tdStyle}>{fmtMargin(row.O1)}</td>
                  <td style={tdStyle}>{fmtMargin(row.O2)}</td>
                  <td style={tdStyle}>{fmtMargin(row.O3)}</td>

                  <td style={tdStyle}>{fmtMargin(row.G0)}</td>
                  <td style={tdStyle}>{fmtMargin(row.G1)}</td>
                  <td style={tdStyle}>{fmtMargin(row.G2)}</td>
                  <td style={tdStyle}>{fmtMargin(row.G3)}</td>

                  <td style={tdStyle}>{fmtPct(row.r01)}</td>
                  <td style={tdStyle}>{fmtPct(row.r12)}</td>
                  <td style={tdStyle}>{fmtPct(row.r03)}</td>

                  <td style={tdStyle}>{fmtPct(row.o01)}</td>
                  <td style={tdStyle}>{fmtPct(row.o12)}</td>
                  <td style={tdStyle}>{fmtPct(row.o03)}</td>

                  <td style={tdStyle}>{fmtPct(row.g01)}</td>
                  <td style={tdStyle}>{fmtPct(row.g12)}</td>
                  <td style={tdStyle}>{fmtPct(row.g03)}</td>

                  <td style={tdStyle}>
                    {row.latestDate
                      ? `${row.latestPeriod || ""} ${row.latestYear || ""} (${row.latestDate})`
                      : "-"}
                  </td>

                  <td
                    style={{
                      ...tdStyle,
                      color: row.error ? "crimson" : "inherit",
                      maxWidth: "260px",
                      whiteSpace: "normal",
                    }}
                  >
                    {row.error || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!sortedRows.length && (
            <div style={{ marginTop: "16px" }}>No matching tickers found.</div>
          )}
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "10px 12px",
  borderBottom: "1px solid #ddd",
  cursor: "pointer",
  position: "sticky",
  top: 0,
  background: "#fff",
  whiteSpace: "nowrap",
  fontSize: "13px",
};

const tdStyle = {
  padding: "10px 12px",
  borderBottom: "1px solid #eee",
  whiteSpace: "nowrap",
  fontSize: "13px",
};

const tdTickerStyle = {
  ...tdStyle,
  fontWeight: 700,
};